import { useEffect, useLayoutEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n/i18n';
import { useSession } from '../../state/session';
import { Icon, my_page } from '../Icon/Icon';
import './AccountPageNavigation.css';

// how much of the neighbouring item to keep visible, as a hint that the menu scrolls
const SCROLL_MARGIN = 24;

// every account page renders its own copy of this menu, so navigating between them remounts it with
// the scroll position reset. remembering the position lets the menu stay put across a navigation,
// and it starts out empty on a real page load, which is when the active item should be revealed
let rememberedScrollLeft: number | null = null;

// the list scrolls on mobile and wraps from 820px, where there is nothing to scroll
const getScroller = (list: HTMLUListElement | null) => {
	if (!list) return null;
	const scroller = list.scrollWidth > list.clientWidth ? list : list.parentElement;
	return scroller && scroller.scrollWidth > scroller.clientWidth ? scroller : null;
};

const revealActiveItem = (list: HTMLUListElement | null) => {
	const scroller = getScroller(list);
	const active = list?.querySelector('li.navigation-item.active');
	if (!scroller || !active) return;

	const scrollerRect = scroller.getBoundingClientRect();
	const activeRect = active.getBoundingClientRect();
	const hiddenToTheLeft = scrollerRect.left - activeRect.left + SCROLL_MARGIN;
	const hiddenToTheRight = activeRect.right - scrollerRect.right + SCROLL_MARGIN;

	// assigning scrollLeft jumps instead of animating, so the menu is simply already in the right
	// place when the page appears. it also avoids scrollBy's options form, absent in Safari 12
	if (hiddenToTheLeft > 0) scroller.scrollLeft -= hiddenToTheLeft;
	else if (hiddenToTheRight > 0) scroller.scrollLeft += hiddenToTheRight;
};

type AccountPageNavigationProps = {};

export const AccountPageNavigation = ({}: AccountPageNavigationProps) => {
	const t = useTranslation().myPagesNavigation;
	const urls = useTranslation().routes;
	const perma = useLocation().pathname;
	const { user } = useSession();
	const listRef = useRef<HTMLUListElement>(null);

	const isActive = {
		myPages: urls.myPages === perma,
		orders: perma.startsWith(urls.yourOrders),
		bonus: urls.bonus === perma,
		favorites: urls.favorites === perma,
		previouslyBought: urls.previouslyBought === perma,
		specialPrices: urls.specialPrices === perma || urls.specialPrices2 === perma,
		account: urls.account === perma,
	};

	// the routes that render this menu, read on unmount to tell a move within my pages from leaving it
	const accountPaths = useRef<string[]>([]);
	accountPaths.current = [
		urls.myPages,
		urls.statistics,
		urls.account,
		urls.bonus,
		urls.favorites,
		urls.previouslyBought,
		urls.specialPrices,
		urls.specialPrices2,
		urls.yourOrders,
		// some markets leave a route empty, and an empty path would match everything below
	].filter(Boolean);

	// place the menu before the first paint, so the scrolling itself is never visible
	const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
	useIsomorphicLayoutEffect(() => {
		const list = listRef.current;
		if (!list) return;

		const scroller = getScroller(list);
		if (rememberedScrollLeft !== null && scroller) {
			scroller.scrollLeft = rememberedScrollLeft;
		} else {
			revealActiveItem(list);
		}

		const remember = () => {
			const current = getScroller(list);
			if (current) rememberedScrollLeft = current.scrollLeft;
		};
		remember();

		// either the list or its container is the one that overflows, so listen on both
		const targets = [list, list.parentElement].filter((el): el is HTMLElement => el !== null);
		targets.forEach((el) => el.addEventListener('scroll', remember, { passive: true }));
		return () => {
			targets.forEach((el) => el.removeEventListener('scroll', remember));
			// a destination within my pages often renders a loader before it mounts its own copy of
			// the menu, so the position is only forgotten once the section itself is left behind
			const { pathname } = window.location;
			const staysInSection = accountPaths.current.some(
				(path) => pathname === path || pathname.startsWith(`${path}/`)
			);
			if (!staysInSection) rememberedScrollLeft = null;
		};
	}, []);

	// the menu only starts scrolling once it stops wrapping, and the labels reflow when the webfont
	// replaces the fallback — both change whether the active item is still within view
	useEffect(() => {
		let cancelled = false;
		// only wait for the swap while it is still pending: once the font has loaded there is no
		// reflow left to correct, and re-running would move a menu that should stay where it is
		const fonts = document.fonts;
		if (fonts && fonts.status !== 'loaded') {
			fonts.ready.then(() => {
				if (!cancelled) revealActiveItem(listRef.current);
			});
		}

		let frame = 0;
		const onResize = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => revealActiveItem(listRef.current));
		};
		window.addEventListener('resize', onResize);

		return () => {
			cancelled = true;
			cancelAnimationFrame(frame);
			window.removeEventListener('resize', onResize);
		};
	}, []);

	// the special prices item appears once the session loads, shifting everything after it. comparing
	// the previous value rather than counting runs keeps a repeated effect from moving the menu
	const previousSpecials = useRef(user?.specials);
	useEffect(() => {
		if (previousSpecials.current === user?.specials) return;
		previousSpecials.current = user?.specials;
		revealActiveItem(listRef.current);
	}, [user?.specials]);

	return (
		<div id="account-page-navigation">
			<ul className="navigation" ref={listRef}>
				<li className={`navigation-item ${isActive.myPages ? 'active' : ''}`}>
					{/* 					<NavLink to={urls.myPages}> */}
					<div className="my-page-items-wrapper" style={{ fontFamily: 'Mabry Pro Bold' }}>
						<Icon size={22} shape={my_page} />
						{t.myPages}
					</div>
					{/* 					</NavLink> */}
				</li>
				{/* <li className={`navigation-item ${urls.statistics === perma ? 'active' : ''}`}>
					<NavLink to={urls.statistics}>{t.statistics}</NavLink>
				</li> */}
				<li className={`navigation-item ${isActive.orders ? 'active' : ''}`}>
					<NavLink to={urls.yourOrders}>{t.orders}</NavLink>
				</li>
				<li className={`navigation-item ${isActive.bonus ? 'active' : ''}`}>
					<NavLink to={urls.bonus}>{t.bonus}</NavLink>
				</li>
				<li className={`navigation-item ${isActive.favorites ? 'active' : ''}`}>
					<NavLink to={urls.favorites}>{t.favorites}</NavLink>
				</li>
				<li className={`navigation-item ${isActive.previouslyBought ? 'active' : ''}`}>
					<NavLink to={urls.previouslyBought}>{t.purchasedItems}</NavLink>
				</li>
				{user?.specials && (
					<li className={`navigation-item ${isActive.specialPrices ? 'active' : ''}`}>
						<NavLink to={urls.specialPrices2}>{t.specialPrices}</NavLink>
					</li>
				)}
				<li className={`navigation-item ${isActive.account ? 'active' : ''}`}>
					<NavLink to={urls.account}>{t.settings}</NavLink>
				</li>
			</ul>
		</div>
	);
};
